import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import api from '../services/api';
import { PlusIcon, PhoneIcon, SignalIcon, XIcon, CheckIcon, RefreshIcon, AlertIcon, TrashIcon } from '../components/Icons';

const Connections = () => {
  const { t } = useI18n();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '', connId: null });
  const [editData, setEditData] = useState({});
  const [logs, setLogs] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumberId: '',
    wabaId: '',
    accessToken: ''
  });

  const addLog = (type, text, details = null) => {
    const newLog = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      type,
      text,
      details
    };
    setLogs(prev => [newLog, ...prev].slice(0, 50)); // Manter 50 entradas para logs detalhados
  };

  const clearLogs = () => {
    setLogs([]);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      addLog('info', t('log.sepLoadConns'));
      addLog('info', t('connection.a1'));
      addLog('info', t('connection.a2'));

      const response = await api.get('/connections');
      setConnections(response.data);

      addLog('info', '---------------------------------------');
      addLog('success', t('connection.a3', { v1: response.data.length }));

      // Verificar status de cada conexão
      response.data.forEach(conn => {
        addLog('info', '---------------------------------------');
        addLog('info', t('connection.a4', { v1: conn.name }));
        addLog('info', `Phone Number ID: ${conn.phoneNumberId}`);
        addLog('info', `WABA ID: ${conn.wabaId || t('log.notConfigured')}`);

        if (conn.status === 'connected') {
          addLog('success', t('connection.a5'));
          if (conn.verifiedName) addLog('info', t('connection.a6', { v1: conn.verifiedName }));
          if (conn.qualityRating) addLog('info', `Qualidade: ${conn.qualityRating}`);
        } else {
          addLog('error', t('connection.a7'));
        }
      });

      addLog('info', '=======================================');
    } catch (error) {
      console.error('Error loading data:', error);
      addLog('error', t('connection.a8'));
      addLog('error', t('connection.a9', { v1: error.message }));
      addLog('info', '=======================================');
    }
    setLoading(false);
  };

  const loadConnections = async () => {
    try {
      const response = await api.get('/connections');
      setConnections(response.data);
    } catch (error) {
      console.error('Error loading connections:', error);
      addLog('error', t('connection.a10'), error.message);
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    const conn = connections.find(c => c.id === id);

    addLog('info', t('log.sepTestConn'));
    addLog('info', t('connection.a11', { v1: conn?.name }));
    addLog('info', `Phone Number ID: ${conn?.phoneNumberId}`);
    addLog('info', `WABA ID: ${conn?.wabaId || t('log.notConfigured')}`);
    addLog('info', '---------------------------------------');
    addLog('info', t('connection.a12'));

    try {
      const response = await api.post(`/connections/${id}/test`);

      // Mostrar REQUEST JSON enviado para Meta
      if (response.data.metaRequest) {
        addLog('request', t('connection.a13'), JSON.stringify(response.data.metaRequest, null, 2));
      }

      addLog('info', '---------------------------------------');

      if (response.data.connected) {
        addLog('success', t('connection.a14'));
        addLog('success', `Telefone: ${response.data.phoneNumber || 'N/A'}`);
        addLog('success', t('connection.a15', { v1: response.data.verifiedName || 'N/A' }));
        addLog('success', `Qualidade: ${response.data.qualityRating || 'N/A'}`);
        addLog('info', '---------------------------------------');

        // Mostrar RESPONSE JSON da Meta
        if (response.data.metaResponse) {
          addLog('success', t('connection.a16'), JSON.stringify(response.data.metaResponse, null, 2));
        }
      } else {
        addLog('error', t('connection.a17'));
        addLog('error', t('connection.a18', { v1: response.data.error || t('log.unknownError') }));
        addLog('info', '---------------------------------------');

        // Mostrar erro completo da Meta
        if (response.data.metaError) {
          addLog('error', t('connection.a19'), JSON.stringify(response.data.metaError, null, 2));
        }
      }

      addLog('info', '=======================================');
      loadConnections();
    } catch (error) {
      addLog('error', t('connection.a20'));
      addLog('error', t('connection.a21', { v1: error.message }));
      if (error.response?.data) {
        addLog('error', 'ERROR PAYLOAD:', JSON.stringify(error.response.data, null, 2));
      }
      addLog('info', '=======================================');
    }
    setTesting(null);
  };

  const handleEditDataChange = (connId, field, value) => {
    setEditData(prev => ({
      ...prev,
      [connId]: {
        ...prev[connId],
        [field]: value
      }
    }));
  };

  const handleSaveConnection = async (conn) => {
    const data = editData[conn.id] || {};

    // Verificar se há alterações (considerar que accessToken vazio significa manter o atual)
    const hasTokenChange = data.accessToken && data.accessToken.trim() !== '';
    const hasWabaChange = data.wabaId !== undefined && data.wabaId !== conn.wabaId;

    if (!hasTokenChange && !hasWabaChange) {
      setSaveMessage({ type: 'info', text: t('connection.a22'), connId: conn.id });
      setTimeout(() => setSaveMessage({ type: '', text: '', connId: null }), 3000);
      return;
    }

    setSaving(conn.id);
    setSaveMessage({ type: '', text: '', connId: null });

    addLog('info', t('log.sepUpdateConn'));
    addLog('info', t('connection.a23', { v1: conn.name }));
    addLog('info', `ID: ${conn.id}`);
    addLog('info', '---------------------------------------');

    try {
      const updateData = {};

      // Só enviar accessToken se foi preenchido (novo token)
      if (hasTokenChange) {
        updateData.accessToken = data.accessToken.trim();
        addLog('info', t('connection.a24', { v1: data.accessToken.substring(0, 20) }));
      }

      // Sempre enviar wabaId se foi alterado
      if (hasWabaChange) {
        updateData.wabaId = data.wabaId;
        addLog('info', `WABA ID: ${conn.wabaId || 'vazio'} -> ${data.wabaId}`);
      }

      addLog('info', t('connection.a25'));
      addLog('request', 'UPDATE REQUEST:', JSON.stringify({
        ...updateData,
        accessToken: updateData.accessToken ? t('connection.a26') : undefined
      }, null, 2));

      const response = await api.put(`/connections/${conn.id}`, updateData);

      addLog('info', '---------------------------------------');
      addLog('success', t('connection.a27'));

      // Se foi atualizado token, verificar conexão
      if (hasTokenChange && response.data.status) {
        if (response.data.status === 'connected') {
          addLog('success', t('connection.a28'));
        } else {
          addLog('warning', t('connection.a29'));
        }
      }

      addLog('info', '=======================================');

      setSaveMessage({ type: 'success', text: t('connection.a30'), connId: conn.id });

      // Limpar dados de edição desta conexão
      setEditData(prev => {
        const newData = { ...prev };
        delete newData[conn.id];
        return newData;
      });

      // Recarregar conexões
      loadConnections();
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('connection.a31');
      addLog('info', '---------------------------------------');
      addLog('error', t('connection.a32'));
      addLog('error', t('connection.a33', { v1: errorMsg }));
      if (error.response?.data) {
        addLog('error', 'DETALHES:', JSON.stringify(error.response.data, null, 2));
      }
      addLog('info', '=======================================');

      setSaveMessage({
        type: 'error',
        text: errorMsg,
        connId: conn.id
      });
    }

    setSaving(null);
    setTimeout(() => setSaveMessage({ type: '', text: '', connId: null }), 5000);
  };

  const handleDeleteConnection = async (conn) => {
    if (!window.confirm(t('connection.a34', { v1: conn.name }))) {
      return;
    }

    setDeleting(conn.id);
    addLog('warning', t('connection.a35', { v1: conn.name }));

    try {
      await api.delete(`/connections/${conn.id}`);
      addLog('success', t('connection.a36', { v1: conn.name }));
      loadConnections();
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('connection.a37');
      addLog('error', t('connection.a38', { v1: errorMsg }));
      alert(errorMsg);
    }

    setDeleting(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    addLog('info', t('log.sepNewConn'));
    addLog('info', t('connection.a39', { v1: formData.name }));
    addLog('info', `Phone Number ID: ${formData.phoneNumberId}`);
    addLog('info', `WABA ID: ${formData.wabaId || t('log.notProvided')}`);
    addLog('info', t('connection.a40', { v1: formData.accessToken.substring(0, 20) }));
    addLog('info', '---------------------------------------');
    addLog('info', t('connection.a41'));

    try {
      const response = await api.post('/connections', formData);

      // Mostrar REQUEST enviado para Meta
      if (response.data.metaRequest) {
        addLog('request', t('connection.a42'), JSON.stringify(response.data.metaRequest, null, 2));
      }

      addLog('info', '---------------------------------------');
      addLog('success', t('connection.a43'));

      // Se a resposta incluir status da conexão
      if (response.data.status === 'connected') {
        addLog('success', t('connection.a44'));

        // Mostrar RESPONSE da Meta
        if (response.data.metaResponse) {
          addLog('success', t('connection.a45'), JSON.stringify(response.data.metaResponse, null, 2));
        }
      } else {
        addLog('warning', t('connection.a46'));

        // Mostrar erro da Meta se houver
        if (response.data.metaError) {
          addLog('error', t('connection.a47'), JSON.stringify(response.data.metaError, null, 2));
        }
      }

      addLog('info', '=======================================');
      setShowModal(false);
      setFormData({ name: '', phoneNumberId: '', wabaId: '', accessToken: '' });
      loadConnections();
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('connection.a48');
      addLog('info', '---------------------------------------');
      addLog('error', t('connection.a49'));
      addLog('error', t('connection.a50', { v1: errorMsg }));
      if (error.response?.data) {
        addLog('error', 'ERROR PAYLOAD:', JSON.stringify(error.response.data, null, 2));
      }
      addLog('info', '=======================================');
      alert(errorMsg);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('conn.title')}</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20"
        >
          <PlusIcon className="w-5 h-5" />{t('connection.j1')}</button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Conexões - 3 colunas */}
        <div className="xl:col-span-3">
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-whatsapp border-t-transparent rounded-full animate-spin"></div>
            <span>{t('common.loading')}</span>
          </div>
        </div>
      ) : connections.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
          <SignalIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 mb-4">{t('conn.empty')}</p>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20"
          >{t('connection.j2')}</button>
        </div>
      ) : (
        <div className="space-y-6">
          {connections.map((conn) => (
            <div key={conn.id} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-whatsapp/10 rounded-xl flex items-center justify-center">
                    <PhoneIcon className="w-7 h-7 text-whatsapp" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{conn.name}</h3>
                    <p className="text-sm text-gray-500">Phone Number ID: {conn.phoneNumberId}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  conn.status === 'connected' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    conn.status === 'connected' ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <span className={`text-sm font-medium ${
                    conn.status === 'connected' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {conn.status === 'connected' ? t('connection.a51') : t('connection.a52')}
                  </span>
                </div>
              </div>

              <hr className="my-6 border-gray-100" />

              {/* Mensagem de salvamento */}
              {saveMessage.text && saveMessage.connId === conn.id && (
                <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${
                  saveMessage.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : saveMessage.type === 'error'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {saveMessage.type === 'success' ? (
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : saveMessage.type === 'error' ? (
                    <AlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  )}
                  <span className="text-sm">{saveMessage.text}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{t('conn.accessToken')}</label>
                  <div className="relative">
                    <textarea
                      value={editData[conn.id]?.accessToken !== undefined ? editData[conn.id].accessToken : ''}
                      onChange={(e) => handleEditDataChange(conn.id, 'accessToken', e.target.value)}
                      placeholder={conn.accessToken ? t('connection.a53', { v1: conn.accessToken.substring(0, 15) }) : t('connection.a54')}
                      className="w-full h-24 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none resize-none font-mono text-sm"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">{t('connection.j3')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    WABA ID (WhatsApp Business Account)
                  </label>
                  <input
                    type="text"
                    value={editData[conn.id]?.wabaId !== undefined ? editData[conn.id].wabaId : (conn.wabaId || '')}
                    onChange={(e) => handleEditDataChange(conn.id, 'wabaId', e.target.value)}
                    placeholder={t('conn.idExample')}
                    className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none font-mono text-sm"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">{t('connection.j4')}</p>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={() => handleDeleteConnection(conn)}
                  disabled={deleting === conn.id}
                  className="flex items-center gap-2 px-5 py-3 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 disabled:opacity-50 transition-colors"
                >
                  {deleting === conn.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('conn.deleting')}</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-4 h-4" />
                      <span>{t('conn.delete')}</span>
                    </>
                  )}
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleTest(conn.id)}
                    disabled={testing === conn.id}
                    className="flex items-center gap-2 px-5 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    <RefreshIcon className={`w-4 h-4 ${testing === conn.id ? 'animate-spin' : ''}`} />
                    {testing === conn.id ? t('connection.a55') : t('connection.a56')}
                  </button>
                  <button
                    onClick={() => handleSaveConnection(conn)}
                    disabled={saving === conn.id}
                    className="flex items-center gap-2 px-5 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark disabled:opacity-50 transition-all shadow-lg shadow-whatsapp/20"
                  >
                    {saving === conn.id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{t('conn.saving')}</span>
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        <span>{t('conn.saveChanges')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </div>

        {/* Painel de Logs - 2 colunas */}
        <div className="xl:col-span-2">
          <div className="bg-gray-900 rounded-2xl shadow-sm border border-gray-800 sticky top-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>{t('connection.j5')}</h3>
              <button
                onClick={clearLogs}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Limpar
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-xs" style={{ maxHeight: '700px' }}>
              {logs.length === 0 ? (
                <div className="text-gray-500 text-center py-8">{t('connection.j6')}<br />{t('connection.j7')}</div>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className={`${log.type === 'request' ? 'flex flex-col gap-1' : 'flex gap-2'}`}>
                    <div className="flex gap-2">
                      <span className="text-gray-500 flex-shrink-0">[{log.timestamp}]</span>
                      <span className={`${
                        log.type === 'success' ? 'text-green-400' :
                        log.type === 'error' ? 'text-red-400' :
                        log.type === 'warning' ? 'text-yellow-400' :
                        log.type === 'request' ? 'text-purple-400' :
                        'text-blue-400'
                      }`}>
                        {log.type === 'success' && '+ '}
                        {log.type === 'error' && 'x '}
                        {log.type === 'warning' && '! '}
                        {log.type === 'info' && '> '}
                        {log.type === 'request' && '^ '}
                        {log.text}
                      </span>
                    </div>
                    {log.details && (
                      <pre className="ml-4 mt-1 p-2 bg-gray-800 rounded text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">
                        {log.details}
                      </pre>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="px-4 py-2 border-t border-gray-800 text-xs text-gray-500">
              {logs.length} {logs.length === 1 ? 'entrada' : 'entradas'}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('conn.new')}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('conn.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('conn.waTitle')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-whatsapp outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('conn.phoneNumberId')}</label>
                <input
                  type="text"
                  value={formData.phoneNumberId}
                  onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-whatsapp outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('conn.wabaId')}</label>
                <input
                  type="text"
                  value={formData.wabaId}
                  onChange={(e) => setFormData({ ...formData, wabaId: e.target.value })}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-whatsapp outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('conn.accessTokenRequired')}</label>
                <textarea
                  value={formData.accessToken}
                  onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                  className="w-full h-24 px-4 py-3 bg-gray-50 rounded-lg focus:ring-2 focus:ring-whatsapp outline-none resize-none"
                  required
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                >{t('connection.j8')}</button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20"
                >{t('connection.j9')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Connections;
